/**
 * 应用根组件
 */
import React from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toaster } from 'sonner'
import { MainLayout } from './components/layouts'
import { SharePage } from './components/share/SharePage'
import { GlobalImageViewer } from './components/preview/GlobalImageViewer'
import { useAppStore } from './store/useAppStore'
import { getLanguageFromPath } from './i18n/config'
import { useSEO } from './hooks/useSEO'
import { useTauriShortcuts } from './hooks/useTauriShortcuts'
import i18n from './i18n/config'

function LanguageRedirect() {
  const location = useLocation()
  const lang = getLanguageFromPath(location.pathname)

  React.useEffect(() => {
    // 检测浏览器语言并重定向
    const browserLang = navigator.language.startsWith('zh') ? 'zh' : 'en'
    const targetLang = lang === 'en' ? 'en' : browserLang

    // 只在根路径时自动重定向，并使用异步方式避免初始闪烁
    if (location.pathname === '/' || location.pathname === '') {
      const timer = setTimeout(() => {
        window.location.replace(`/${targetLang}`)
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [location.pathname, lang])

  return null
}

function AppContent() {
  const { t } = useTranslation()
  const { documents, setCurrentDocument } = useAppStore()

  // 首次加载时创建欢迎文档
  const isEmpty = documents.length === 0
  const WELCOME_DOC_ID = -1

  const welcomeDoc = React.useMemo(() => {
    const now = Date.now()
    return {
      id: WELCOME_DOC_ID,
      title: t('welcome.title'),
      content: t('welcome.content'),
      created: now,
      updated: now
    }
  }, [t])

  React.useEffect(() => {
    if (isEmpty) {
      setCurrentDocument(welcomeDoc)
    }
  }, [isEmpty, welcomeDoc, setCurrentDocument])

  return (
    <MainLayout />
  )
}

function App() {
  const { theme } = useAppStore()
  const location = useLocation()
  useSEO()
  
  // 启用 Tauri 桌面端快捷键（Cmd/Ctrl+S 保存，Cmd/Ctrl+O 打开）
  useTauriShortcuts()

  // 应用主题到 document root - 使用 data-theme 属性
  React.useEffect(() => {
    const applyTheme = (t: typeof theme) => {
      let effectiveTheme = t
      if (t === 'auto') {
        effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      }
      document.documentElement.setAttribute('data-theme', effectiveTheme)
    }

    applyTheme(theme)

    // 如果是自动模式，监听系统主题变化
    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = (e: MediaQueryListEvent) => {
        document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light')
      }
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [theme])

  // 同步语言到 i18n 和 HTML 属性
  React.useEffect(() => {
    const lang = getLanguageFromPath(location.pathname)
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en'
    // 同步 i18n 语言
    i18n.changeLanguage(lang)
  }, [location.pathname])

  return (
    <>
      <LanguageRedirect />
      <Routes>
        <Route path="/" element={<Navigate to="/en" replace />} />
        <Route path="/:lang" element={<AppContent />} />
        <Route path="/share/:shareId" element={<SharePage />} />
      </Routes>
      <GlobalImageViewer />
      <Toaster
        position="top-center"
        theme={theme === 'auto' ? 'system' : theme as 'light' | 'dark'}
        closeButton
        toastOptions={{
          className: 'notion-toast',
          style: {
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-light)',
            boxShadow: 'var(--shadow-md)',
            borderRadius: '8px',
          },
        }}
      />
    </>
  )
}

export default App
